<template>
  <!-- Subscription state messages -->
  <span v-if="state === 'unpaid'" v-html="unpaidFinishSubscriptionMessage" />
  <span
    v-if="
      state === 'regular' && newsletter.paid_subscriptions_status !== 'active'
    "
    class="confirmation-email-message"
    v-html="subscribedViaMessage"
  />
  <span
    v-if="
      state === 'regular' && newsletter.paid_subscriptions_status === 'active'
    "
    v-html="regularCanUpgradeMessage"
  />
  <span v-else-if="hasStateLabel">
    {{ stateLabel(state) }}
  </span>

  <!-- Action buttons based on state -->
  <ActionButton
    v-if="state === 'regular' && manageSubscriptionUrl"
    :text="t('web.subscription.action.manage')"
    color="blue"
    :href="manageSubscriptionUrl"
  />

  <ActionButton
    v-if="state === 'pending_validation'"
    :text="t('web.subscription.action.validating')"
    color="blue"
    pending
  />

  <ActionButton
    v-if="state === 'pending_subscription'"
    :text="pendingButtonText"
    color="blue"
    pending
  />

  <ActionButton
    v-if="
      state === 'not_subscribed' ||
      (state === 'invalid_ip_address' && turnstileResponseToken)
    "
    :text="subscribeButtonText"
    color="blue"
    :disabled="formDisabled"
    @click="handleSubscribeClick"
  />

  <ActionButton
    v-if="state === 'subscribed_elsewhere'"
    :text="subscribeAsLabel"
    color="blue"
    @click="emit('subscribe')"
  />

  <ActionButton
    v-if="state === 'unsubscribed'"
    :text="t('web.button.resubscribe')"
    color="yellow"
    @click="subscriber && emit('resubscribe', subscriber.id)"
  />

  <ActionButton
    v-if="state === 'invalid_email__regex'"
    :text="t('web.form.validation.email_malformed')"
    color="red"
    disabled
  />

  <ActionButton
    v-if="state === 'invalid_subscription'"
    :text="t('web.form.validation.email_didnt_work')"
    color="red"
    disabled
  />

  <!-- Unactivated: confirmation email sent + sniper link -->
  <div v-if="state === 'unactivated'" class="unactivated-section">
    <div class="confirmation-email-message">
      {{ t("web.subscription.message.confirmation_email_sent") }}
      <strong>{{ emailAddress }}</strong
      >.
    </div>
    <slot name="sniper-link" />
  </div>

  <!-- Upgrade section (trialed / unpaid / regular+paid) -->
  <template v-if="showUpgradeSection">
    <PaymentElement
      v-if="IMMEDIATELY_SHOW_PAYMENT_ELEMENT && showUpgradePayment"
      :newsletter="newsletter"
      :email-address="emailAddress"
      already-subscribed
    />
    <ActionButton
      v-if="IMMEDIATELY_SHOW_PAYMENT_ELEMENT && !showUpgradePayment"
      :text="
        state === 'trialed'
          ? t('web.button.upgrade_subscription')
          : t(upgradeText)
      "
      :color="state === 'trialed' ? 'yellow' : 'blue'"
      @click="showUpgradePayment = true"
    />
    <ActionButton
      v-else-if="!IMMEDIATELY_SHOW_PAYMENT_ELEMENT && upgradeUrl"
      :href="upgradeUrl"
      :text="
        state === 'trialed'
          ? t('web.button.upgrade_subscription')
          : t(upgradeText)
      "
      :color="state === 'trialed' ? 'yellow' : 'blue'"
    />
  </template>

  <!-- Manage subscription -->
  <ActionButton
    v-if="manageStates.includes(state) && manageSubscriptionUrl"
    :href="manageSubscriptionUrl"
    :text="t('web.subscription.action.manage')"
    color="blue"
  />

  <!-- Archive links -->
  <ActionButton
    v-if="state === 'churning' && archiveUrl"
    :href="archiveUrl"
    :text="t('web.button.view_archives')"
    color="yellow"
  />
  <ActionButton
    v-if="
      archiveStates.includes(state) &&
      archiveUrl &&
      variant === 'subscribe_page'
    "
    :href="archiveUrl"
    :text="t('web.button.view_archives')"
    color="blue"
  />

  <!-- Error -->
  <ActionButton
    v-if="state === 'errored_email'"
    :text="error || t('web.message.error.generic')"
    color="red"
  />

  <!-- Awaiting confirmation -->
  <ActionButton
    v-if="state === 'awaiting_email_address_confirmation'"
    :text="t('web.subscription.action.email_confirmed')"
    color="red"
    @click="emit('checkSubscriber')"
  />

  <!-- Contact author -->
  <ActionButton
    v-if="
      state === 'undeliverable' ||
      state === 'pending_email_address_confirmation'
    "
    :href="`mailto:${newsletter.email_address}`"
    :text="t('web.action.contact_author')"
    color="red"
  />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from "vue";

import type { Newsletter } from "@/types/newsletter";

import { type TranslationKey, createTranslator } from "../../lib/utils";
import {
  type BasicSubscriber,
  type SubscribeFormVariant,
  type SubscriptionState,
  mutableUrl,
  shouldImmediatelyShowPaymentElement,
} from "../lib";
import ActionButton from "./ActionButton.vue";

const PaymentElement = defineAsyncComponent(
  () => import("./PaymentElement.vue")
);

const props = defineProps<{
  newsletter: Newsletter;
  state: SubscriptionState;
  subscriber?: BasicSubscriber;
  emailAddress: string;
  variant: SubscribeFormVariant;
  error?: string;
  turnstileResponseToken: string;
  formDisabled: boolean;
  upgradeUrl: string | null;
}>();

const emit = defineEmits<{
  subscribe: [];
  resubscribe: [subscriberId: string];
  checkSubscriber: [];
  highlightRequired: [];
}>();

const t = createTranslator(() => props.newsletter.locale);

const manageSubscriptionUrl = computed(() => {
  if (!props.newsletter.enabled_features.includes("portal")) return null;
  if (!props.subscriber) return null;
  return `https://buttondown.com/${props.newsletter.username}/management/subscribers/${props.subscriber.id}`;
});

const archiveUrl = computed(() => {
  if (!props.newsletter.enabled_features.includes("archives")) return null;
  return mutableUrl(props.newsletter, props.subscriber, "/archive");
});

const upgradeText = computed<TranslationKey>(() => {
  if (props.variant === "archive_page") return "web.button.read_rest";
  if (props.variant === "paywalled_archive_page") return "web.button.log_in";
  return "web.button.upgrade_subscription";
});

const escapeHtml = (str: string) =>
  str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const subscribedViaMessage = computed(() => {
  return t("web.subscription.message.subscribed_via")
    .replace(
      "{newsletter}",
      `<strong>${escapeHtml(props.newsletter.name)}</strong>`
    )
    .replace("{email}", `<strong>${escapeHtml(props.emailAddress)}</strong>`);
});

const unpaidFinishSubscriptionMessage = computed(() =>
  t("web.subscription.message.unpaid_finish_subscription").replace(
    "{newsletter}",
    `<strong>${escapeHtml(props.newsletter.name)}</strong>`
  )
);

const regularCanUpgradeMessage = computed(() =>
  t("web.subscription.message.regular_can_upgrade").replace(
    "{newsletter}",
    `<strong>${escapeHtml(props.newsletter.name)}</strong>`
  )
);

const subscribeAsLabel = computed(() =>
  t("web.action.subscribe_as").replace("{email}", props.emailAddress)
);

const IMMEDIATELY_SHOW_PAYMENT_ELEMENT = shouldImmediatelyShowPaymentElement(
  props.newsletter
);

const showUpgradePayment = ref(false);

const subscribeButtonText = computed(() => {
  if (props.newsletter.subscription_button_text) {
    return props.newsletter.subscription_button_text;
  }
  const trialDuration = props.newsletter.paid_subscription_free_trial_duration;
  if (trialDuration) {
    return t("web.subscription.action.subscribe_free_trial_days").replace(
      "{{ newsletter.paid_subscription_free_trial_duration }}",
      String(trialDuration)
    );
  }
  if (props.variant === "paywalled_archive_page") {
    return t("web.button.log_in");
  }
  return t("web.button.subscribe");
});

const pendingButtonText = computed(() => {
  if (props.newsletter.subscription_button_text) {
    return props.newsletter.subscription_button_text;
  }
  if (props.newsletter.paid_subscription_free_trial_duration) {
    return t("web.subscription.action.subscribe_free_trial");
  }
  return t("web.button.subscribe");
});

function handleSubscribeClick() {
  if (props.formDisabled) {
    emit("highlightRequired");
  } else {
    emit("subscribe");
  }
}

const STATE_TO_LABEL_KEY: Partial<Record<SubscriptionState, TranslationKey>> = {
  churned: "web.subscription.message.churned",
  churning: "web.subscription.message.churning",
  gifted: "web.subscription.message.gifted",
  past_due: "web.subscription.message.past_due",
  paused: "web.subscription.message.paused",
  premium: "web.subscription.message.premium_logged_in",
  unsubscribed: "web.subscription.message.unsubscribed_from",
};

const hasStateLabel = computed(() => Boolean(STATE_TO_LABEL_KEY[props.state]));

function stateLabel(state: SubscriptionState): string {
  const key = STATE_TO_LABEL_KEY[state];
  if (!key) return "";
  return t(key).replace("{newsletter}", props.newsletter.name);
}

const showUpgradeSection = computed(() => {
  return (
    props.state === "trialed" ||
    props.state === "unpaid" ||
    (props.state === "regular" &&
      props.newsletter.paid_subscriptions_status === "active")
  );
});

const manageStates: SubscriptionState[] = [
  "churned",
  "churning",
  "past_due",
  "paused",
  "premium",
];

const archiveStates: SubscriptionState[] = ["churning", "gifted", "premium"];
</script>
